import * as Sentry from '@sentry/react';

/**
 * 과제 풀이(solve) 페이지 전용 에러 트래킹 헬퍼.
 *
 * 이 페이지는 학생이 답안을 제출하는 핵심 경로라 조용한 실패/화이트스크린이
 * 절대 없어야 한다. 모든 위험 지점(DOM 파싱, querySelector 색칠, 제출 mutation,
 * 쿼리 실패, 렌더 크래시)에서 일관된 컨텍스트로 Sentry에 보고하기 위한 유틸.
 */

export const SOLVE_FEATURE = 'assignment-solve';

export type SolveContext = Record<string, unknown>;

/**
 * solve 페이지에 머무는 동안 현재 scope에 과제 컨텍스트 태그를 박는다.
 * 이 동안 발생하는 모든 이벤트(렌더 크래시/쿼리 실패/수동 capture)에
 * assignmentId·courseId 태그가 자동으로 따라붙는다. cleanup 함수를 반환하므로
 * useEffect에서 그대로 반환해 언마운트 시 해제한다.
 */
export function beginSolveScope(ctx: { assignmentId?: string; courseId?: string }) {
  const scope = Sentry.getCurrentScope();
  scope.setTag('feature', SOLVE_FEATURE);
  if (ctx.assignmentId) scope.setTag('assignmentId', ctx.assignmentId);
  if (ctx.courseId) scope.setTag('courseId', ctx.courseId);
  scope.setContext('solve', ctx);

  return () => {
    const s = Sentry.getCurrentScope();
    s.setTag('feature', undefined);
    s.setTag('assignmentId', undefined);
    s.setTag('courseId', undefined);
    s.setContext('solve', null);
  };
}

/**
 * 임의의 throw 값을 Error 인스턴스로 정규화한다.
 * 객체가 던져진 경우 `String(error)`는 "[object Object]"가 되어 원인이 사라지므로,
 * message/code를 끌어내거나 JSON 직렬화해 의미 있는 메시지를 남긴다.
 */
function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;

  if (error != null && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    const message =
      typeof obj.message === 'string' && obj.message
        ? obj.message
        : safeStringify(obj);
    const normalized = new Error(message);
    if (obj.code !== undefined) {
      (normalized as Error & { code?: unknown }).code = obj.code;
    }
    return normalized;
  }

  return new Error(String(error));
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * try/catch 지점에서 호출. 발생 위치(where)와 추가 컨텍스트를 함께 보고한다.
 * 전역 핸들러에 안 잡히는 곳(이펙트 내부, mutation onError 등)에서 사용.
 */
export function captureSolveError(error: unknown, where: string, ctx: SolveContext = {}) {
  Sentry.withScope((scope) => {
    scope.setTag('feature', SOLVE_FEATURE);
    scope.setTag('solve.where', where);
    scope.setContext('solve.detail', ctx);
    scope.setLevel('error');
    Sentry.captureException(normalizeError(error));
  });
}

/** 에러 직전 사용자 행동 흐름을 남기는 breadcrumb. capture 시 함께 전송된다. */
export function solveBreadcrumb(message: string, data?: SolveContext) {
  Sentry.addBreadcrumb({
    category: SOLVE_FEATURE,
    message,
    level: 'info',
    data,
  });
}
